import React, { useState, useContext, useEffect } from 'react';
import { X, Save, Plus, Calendar, User } from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import riskService from '../../../../services/riskService';
import userService from '../../../../services/userService';

export default function UpdateRiskModal({ risk, onClose, onUpdateSuccess }) {
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const [updating, setUpdating] = useState(false);

  const [updateFormData, setUpdateFormData] = useState({
    probabilite: '',
    consequence: ''
  });

  const [updateActions, setUpdateActions] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [currentActionIndex, setCurrentActionIndex] = useState(null);

  const probabiliteOptions = [
    { value: 'mineur', label: 'Mineur', color: 'bg-blue-100 text-blue-800' },
    { value: 'normal', label: 'Normal', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'majeur', label: 'Majeur', color: 'bg-orange-100 text-orange-800' },
    { value: 'critique', label: 'Critique', color: 'bg-red-100 text-red-800' }
  ];

  // Initialize form data when risk changes
  useEffect(() => {
    if (risk) {
      setUpdateFormData({
        probabilite: risk.probability || '',
        consequence: risk.consequence || ''
      });
      
      // Map existing actions to update format
      const mappedActions = risk.actions && risk.actions.length > 0 
        ? risk.actions.map(action => ({
            id: action.id,
            description: action.action,
            assignedUser: action.assigned_user || null,
            assignedUserDisplay: action.assigned_user 
              ? `${action.assigned_user.first_name} ${action.assigned_user.last_name} (${action.assigned_user.email})`
              : '',
            dueDate: action.dueDate || ''
          }))
        : [{
            description: '',
            assignedUser: null,
            assignedUserDisplay: '',
            dueDate: ''
          }];
      
      setUpdateActions(mappedActions);
    }
  }, [risk]);

  const handleUpdateInputChange = (field, value) => {
    setUpdateFormData({ ...updateFormData, [field]: value });
  };

  const handleUpdateActionChange = (index, field, value) => {
    const newActions = [...updateActions];
    newActions[index] = { ...newActions[index], [field]: value };
    setUpdateActions(newActions);
  };

  const addUpdateAction = () => {
    setUpdateActions([...updateActions, { 
      description: '', 
      assignedUser: null,
      assignedUserDisplay: '',
      dueDate: '' 
    }]);
  };

  const removeUpdateAction = (index) => {
    if (updateActions.length > 1) {
      setUpdateActions(updateActions.filter((_, i) => i !== index));
    }
  };

  const searchUsers = async (query, actionIndex) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowUserDropdown(false);
      return;
    }

    try {
      const result = await userService.filterUsersBySearch(query, authTokens.access);
      if (result.success) {
        setSearchResults(result.data);
        setShowUserDropdown(true);
        setCurrentActionIndex(actionIndex);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
      setShowUserDropdown(false);
    }
  };

  const handleUserSearchInput = (query, actionIndex) => {
    const newActions = [...updateActions];
    newActions[actionIndex] = { 
      ...newActions[actionIndex], 
      assignedUserDisplay: query,
      assignedUser: null
    };
    setUpdateActions(newActions);
    
    setUserSearchQuery(query);
    searchUsers(query, actionIndex);
  };

  const selectUser = (selectedUser, actionIndex) => {
    const userDisplay = `${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})`;
    
    const newActions = [...updateActions];
    newActions[actionIndex] = {
      ...newActions[actionIndex],
      assignedUser: selectedUser,
      assignedUserDisplay: userDisplay
    };
    setUpdateActions(newActions);
    
    setUserSearchQuery('');
    setShowUserDropdown(false);
    setSearchResults([]);
    setCurrentActionIndex(null);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);

    const validActions = updateActions.filter(action => action.description.trim() !== '');

    const processedActions = validActions.map(action => ({
      id: action.id || null,
      action: action.description,
      assignedUserId: action.assignedUser ? action.assignedUser.id : null,
      userRoleAssignedId: action.assignedUser?.role_function ? action.assignedUser.id : null,
      dueDate: action.dueDate || null,
    }));

    const backendData = {
      probability: updateFormData.probabilite,
      consequence: updateFormData.consequence,
      actions: processedActions,
    };

    console.log('Updating risk:', risk.id, backendData);

    try {
      const result = await riskService.updateActivityRisk(risk.id, backendData, authTokens.access);

      if (result.success) {
        let successMessage = "Risque mis à jour avec succès !";
        
        // Check if there were task updates in the response
        if (result.data && result.data.changes && result.data.changes.actions) {
          const actionsUpdated = result.data.changes.actions.updated || [];
          let totalTasksReassigned = 0;
          
          actionsUpdated.forEach(actionUpdate => {
            if (actionUpdate.changes && actionUpdate.changes.task_updates) {
              totalTasksReassigned += actionUpdate.changes.task_updates.tasks_updated || 0;
            }
          });
          
          if (totalTasksReassigned > 0) {
            successMessage += ` ${totalTasksReassigned} tâche(s) associée(s) ont été réassignées automatiquement.`;
          }
        }
        
        showSuccess(successMessage);
        onUpdateSuccess();
      } else {
        console.error('Backend error:', result.errorData);
        showError(result.error || "Erreur lors de la mise à jour du risque");
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      showError("Erreur inattendue. Veuillez réessayer.");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Modifier le risque: {risk.risk_name}
          </h3>
          <button
            onClick={onClose}
            type="button"
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Consequence */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conséquence <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={updateFormData.consequence}
                onChange={(e) => handleUpdateInputChange('consequence', e.target.value)}
                placeholder="Décrire la conséquence du risque..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                required
              />
            </div>

            {/* Probability */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Probabilité <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {probabiliteOptions.map(option => (
                  <label
                    key={option.value}
                    className={`flex items-center justify-center p-3 border rounded-lg cursor-pointer ${
                      updateFormData.probabilite === option.value
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="probabilite"
                      value={option.value}
                      checked={updateFormData.probabilite === option.value}
                      onChange={(e) => handleUpdateInputChange('probabilite', e.target.value)}
                      className="sr-only"
                    />
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${option.color}`}>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">
                  Actions préventives/correctives
                </label>
                <button
                  type="button"
                  onClick={addUpdateAction}
                  className="flex items-center px-3 py-1 text-sm bg-teal-100 text-teal-700 rounded-md hover:bg-teal-200"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter une action
                </button>
              </div>

              <div className="space-y-4">
                {updateActions.map((action, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Action {index + 1}</h4>
                      {updateActions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeUpdateAction(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded-md"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-3">
                      {/* Action Description */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Description de l'action
                        </label>
                        <textarea
                          value={action.description}
                          onChange={(e) => handleUpdateActionChange(index, 'description', e.target.value)}
                          placeholder={`Décrire l'action ${index + 1}...`}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* User Assignment */}
                        <div className="relative">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            <User className="w-3 h-3 inline mr-1" />
                            Utilisateur assigné
                          </label>
                          <input
                            type="text"
                            value={action.assignedUserDisplay || ''}
                            onChange={(e) => handleUserSearchInput(e.target.value, index)}
                            placeholder="Rechercher un utilisateur (min 2 caractères)..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm"
                          />

                          {/* User Search Dropdown */}
                          {showUserDropdown && currentActionIndex === index && searchResults.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto">
                              {searchResults.map((user) => (
                                <button
                                  key={user.id}
                                  type="button"
                                  onClick={() => selectUser(user, index)}
                                  className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm"
                                >
                                  <div className="font-medium">
                                    {user.first_name} {user.last_name} <span className="text-gray-500 text-xs">{user.role_function}</span>
                                  </div>
                                  <div className="text-gray-500 text-xs">
                                    {user.email}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Due Date */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            Date d'échéance
                          </label>
                          <input
                            type="date"
                            value={action.dueDate}
                            onChange={(e) => handleUpdateActionChange(index, 'dueDate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                        </div>
                      </div>

                      {/* Show selected user info */}
                      {action.assignedUser && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                          <p className="text-xs text-blue-900">
                            <strong>Rôle:</strong> {action.assignedUser.role_function}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleUpdateSubmit}
                disabled={updating}
                className="flex items-center px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400"
              >
                <Save className="h-4 w-4 mr-2" />
                {updating ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}